/** Outage detail panel (spec §8): affected area + trust summary, the PAGED
 *  affected-meter/address list, the event timeline (restoration log), and the
 *  operator ACTION set.
 *
 *  Operator actions are ROLE-AWARE: only dispatcher+ (an `oms.dispatch` or
 *  `oms.admin` grant, see core/authz.py) sees the mutation controls; viewers
 *  get a read-only panel. The server is the real gate — this is a UX nicety.
 *
 *  A CLOSED outage stays reviewable: the engine auto-closes most outages when
 *  power returns, so Cause, Confirm and Cancel (false positive) remain
 *  available after the fact. Everything else — and every action on a CANCELLED outage — is
 *  locked, matching the server's state-transition guards. */

import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth.ts'
import {
  useAffectedMeters,
  useOutage,
  useOutageTimeline,
} from '../../hooks/useOms.ts'
import type { OutageDetail } from '../../types/oms.ts'
import { REPORT_SOURCE_LABELS } from '../../types/oms.ts'
import { canDispatch } from '../../utils/capabilities.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import {
  CriticalCountBadge,
  PriorityBadge,
  ScopeBadge,
  SourceBadge,
  StatusBadge,
  TrustBadge,
  causeText,
  deviceLabel,
  formatAgo,
  formatDateTime,
  formatDuration,
} from './omsFormat.tsx'
import {
  CauseDialog,
  EditOutageDialog,
  MergeOutageDialog,
  OutageStateDialog,
  SplitOutageDialog,
} from './OperatorActions.tsx'
import { CorrelatedEventsBanner } from './CorrelatedEventsBanner.tsx'
import { DispatchCard } from './DispatchCard.tsx'
import { SuggestedSourceBanner } from './SuggestedSourceBanner.tsx'

const METERS_PAGE_SIZE = 20

// =========================================================================
// Affected meters — paged (PII; never unbounded)
// =========================================================================

function AffectedMeters({ outageId }: { outageId: number }) {
  const [q, setQ] = useState('')
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
  const [page, setPage] = useState(0)

  const { data, isLoading, isError } = useAffectedMeters(outageId, {
    q: q || undefined,
    limit: METERS_PAGE_SIZE,
    cursor: cursorStack[page] ?? undefined,
  })

  const items = data?.items ?? []
  const hasNext = data?.next_cursor != null

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Affected meters</h4>
        <input
          value={q}
          onChange={e => {
            setQ(e.target.value)
            setCursorStack([null])
            setPage(0)
          }}
          placeholder="Search meter / address"
          className="text-xs border border-gray-300 rounded-md px-2 py-1 w-48"
        />
      </div>
      {isLoading ? (
        <div className="text-xs text-gray-400 py-4 text-center">Loading meters…</div>
      ) : isError ? (
        <div className="text-xs text-red-600 py-4 text-center">
          Failed to load affected meters.
        </div>
      ) : items.length === 0 ? (
        <div className="text-xs text-gray-400 py-4 text-center">
          No affected meters listed.
        </div>
      ) : (
        <div className="overflow-auto max-h-64 border border-gray-100 rounded-md">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="p-1.5">Meter</th>
                <th className="p-1.5">Address</th>
                <th className="p-1.5">Basis</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m, i) => (
                <tr key={`${m.meter_id ?? 'x'}-${i}`} className="border-b border-gray-50">
                  <td className="p-1.5 font-mono">
                    {m.meter_number ?? (m.meter_id != null ? `#${m.meter_id}` : '--')}
                  </td>
                  <td className="p-1.5 text-gray-600 max-w-[220px] truncate" title={m.address ?? ''}>
                    {m.address ?? '--'}
                  </td>
                  <td className="p-1.5 text-gray-400">{m.impact_basis ?? '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <button
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
          className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-[10px] text-gray-400">Page {page + 1}</span>
        <button
          onClick={() => {
            if (!hasNext || data?.next_cursor == null) return
            setCursorStack(prev => {
              const next = prev.slice(0, page + 1)
              next.push(data.next_cursor)
              return next
            })
            setPage(p => p + 1)
          }}
          disabled={!hasNext}
          className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  )
}

// =========================================================================
// Timeline (restoration log)
// =========================================================================

function Timeline({ outageId }: { outageId: number }) {
  const { data, isLoading, isError } = useOutageTimeline(outageId)

  if (isLoading) {
    return <div className="text-xs text-gray-400 py-4 text-center">Loading timeline…</div>
  }
  if (isError) {
    return (
      <div className="text-xs text-red-600 py-4 text-center">
        Failed to load timeline.
      </div>
    )
  }
  if (!data || data.length === 0) {
    return (
      <div className="text-xs text-gray-400 py-4 text-center">
        No timeline entries yet.
      </div>
    )
  }
  return (
    <ol className="relative border-l border-gray-200 ml-2 space-y-3">
      {data.map(entry => (
        <li key={entry.id} className="ml-4">
          <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
          <div className="text-xs font-medium text-gray-700">
            {entry.action.replace(/_/g, ' ')}
          </div>
          <div className="text-[10px] text-gray-400">
            {formatDateTime(entry.action_time)}
            {entry.performed_by ? ` · ${entry.performed_by}` : ''}
          </div>
          {entry.notes && <div className="text-xs text-gray-600 mt-0.5">{entry.notes}</div>}
        </li>
      ))}
    </ol>
  )
}

// =========================================================================
// Operator action bar (role-aware)
// =========================================================================

type ActiveDialog =
  | 'edit'
  | 'cause'
  | 'close'
  | 'cancel'
  | 'confirm'
  | 'merge'
  | 'split'
  | null

function ActionBar({
  outage,
  onDone,
}: {
  outage: OutageDetail
  onDone: (detail: OutageDetail) => void
}) {
  const [dialog, setDialog] = useState<ActiveDialog>(null)
  const isClosed = outage.status === 'closed'
  const isCancelled = outage.status === 'cancelled'
  // Close / edit / merge / split only make sense while the outage is open.
  const isTerminal = isClosed || isCancelled
  const terminalTitle = isTerminal ? `Outage is ${outage.status}` : undefined
  const isConfirmed = outage.confirmed_at !== null

  return (
    <>
      {isClosed && (
        <p className="text-xs text-gray-600 mb-2">
          Closed {formatDateTime(outage.end_time)}. You can still set its
          cause, confirm it{isConfirmed ? ' (already done)' : ''} or cancel it
          as a false positive.
        </p>
      )}
      {isCancelled && (
        <p className="text-xs text-gray-600 mb-2">
          Cancelled as a false positive — excluded from reliability metrics.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setDialog('edit')}
          disabled={isTerminal}
          title={terminalTitle}
          className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Edit ETR / priority…
        </button>
        <button
          onClick={() => setDialog('cause')}
          disabled={isCancelled}
          title={isCancelled ? 'Outage is cancelled' : 'PowerTRX cause, for EIA reporting'}
          className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {outage.cause_detail ? 'Change cause…' : 'Set cause…'}
        </button>
        <button
          onClick={() => setDialog('confirm')}
          disabled={isCancelled || isConfirmed}
          className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          title={
            isConfirmed
              ? 'Already confirmed'
              : isCancelled
                ? 'A cancelled false positive cannot be confirmed'
                : 'Confirm as a real outage'
          }
        >
          Confirm
        </button>
        <button
          onClick={() => setDialog('close')}
          disabled={isTerminal}
          title={terminalTitle}
          className="text-xs px-3 py-1.5 rounded-md bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-40"
        >
          Close
        </button>
        <button
          onClick={() => setDialog('cancel')}
          disabled={isCancelled}
          title={isCancelled ? 'Already cancelled' : undefined}
          className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
        >
          Cancel (false positive)
        </button>
        <button
          onClick={() => setDialog('merge')}
          disabled={isTerminal}
          title={terminalTitle}
          className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Merge…
        </button>
        <button
          onClick={() => setDialog('split')}
          disabled={isTerminal}
          title={terminalTitle}
          className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Split…
        </button>
      </div>

      {(dialog === 'close' || dialog === 'cancel' || dialog === 'confirm') && (
        <OutageStateDialog
          action={dialog}
          outage={outage}
          onClose={() => setDialog(null)}
          onDone={onDone}
        />
      )}
      {dialog === 'cause' && (
        <CauseDialog
          outage={outage}
          onClose={() => setDialog(null)}
          onDone={onDone}
        />
      )}
      {dialog === 'edit' && (
        <EditOutageDialog
          outage={outage}
          onClose={() => setDialog(null)}
          onDone={onDone}
        />
      )}
      {dialog === 'merge' && (
        <MergeOutageDialog
          primary={outage}
          onClose={() => setDialog(null)}
          onDone={onDone}
        />
      )}
      {dialog === 'split' && (
        <SplitOutageDialog
          outage={outage}
          onClose={() => setDialog(null)}
          onDone={onDone}
        />
      )}
    </>
  )
}

// =========================================================================
// Detail panel
// =========================================================================

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-gray-700">{value}</div>
    </div>
  )
}

export function OutageDetailPanel({
  outageId,
  onClose,
  onNavigate,
  onLocate,
}: {
  outageId: number
  onClose: () => void
  onNavigate: (id: number) => void
  /** Fly the outage map to this outage's source point (spec 2026-09-01 §D). */
  onLocate?: (id: number) => void
}) {
  const { user } = useAuth()
  const canMutate = canDispatch(user?.capabilities)
  const { data: outage, isLoading, isError, error } = useOutage(outageId)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">
          Outage detail{outage ? ` — #${outage.id}` : ''}
        </h3>
        <div className="flex items-center gap-2">
          {onLocate && (
            <button
              type="button"
              onClick={() => onLocate(outageId)}
              title="Locate on map"
              aria-label="Locate on map"
              className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              ⌖ Locate
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-lg leading-none"
            aria-label="Close detail"
          >
            &times;
          </button>
        </div>
      </div>

      <div className="p-4 overflow-auto space-y-5">
        {isLoading && <div className="text-sm text-gray-400 py-6 text-center">Loading…</div>}
        {isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            {friendlyError(error, 'load the outage')}
          </div>
        )}
        {outage && (
          <>
            {/* Summary + trust */}
            <div className="flex flex-wrap items-center gap-3">
              <ScopeBadge scope={outage.scope} />
              <StatusBadge status={outage.status} />
              <TrustBadge item={outage} />
              <SourceBadge item={outage} />
              <CriticalCountBadge
                count={outage.critical_customers_affected}
                protectedFlag={outage.critical_customers_protected}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Affected area"
                value={deviceLabel(
                  outage.scope,
                  outage.device_id,
                  outage.unresolved_meter_number,
                  outage.suggestion_detail,
                )}
              />
              <Field
                label="Customers affected"
                value={outage.customers_affected ?? '--'}
              />
              <Field label="Started" value={formatDateTime(outage.start_time)} />
              <Field label="Duration" value={formatDuration(outage.duration_seconds)} />
              <Field label="Last signal" value={formatAgo(outage.last_signal_time)} />
              <Field
                label="Meters out / total"
                value={`${outage.meters_reporting_out ?? '--'} / ${outage.meters_total ?? '--'}`}
              />
              <Field label="Cause" value={causeText(outage)} />
              {outage.report_source && (
                <Field label="Reported by" value={REPORT_SOURCE_LABELS[outage.report_source]} />
              )}
              <Field label="Priority" value={<PriorityBadge priority={outage.priority} />} />
              <Field
                label="Est. restoration"
                value={
                  outage.estimated_restoration_at
                    ? formatDateTime(outage.estimated_restoration_at)
                    : '--'
                }
              />
              <Field label="Assigned crew" value={outage.assigned_crew ?? '--'} />
              <Field
                label="Confirmed"
                value={
                  outage.confirmed_at
                    ? `${formatDateTime(outage.confirmed_at)}${outage.confirmed_by ? ` · ${outage.confirmed_by}` : ''}`
                    : 'No'
                }
              />
              {outage.parent_event_id != null && (
                <Field label="Parent event" value={`#${outage.parent_event_id}`} />
              )}
            </div>

            {outage.notes && (
              <div>
                <div className="text-[10px] text-gray-400 uppercase tracking-wide">Notes</div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{outage.notes}</div>
              </div>
            )}

            {/* Probable source (spec §4.2) — engine suggestion + confirm/override */}
            <SuggestedSourceBanner outage={outage} canMutate={canMutate} />

            {/* Where to send the crew (spec 2026-09-01 §D) */}
            <DispatchCard location={outage.dispatch_location} />

            {/* "Likely same outage" time-correlation hint */}
            <CorrelatedEventsBanner
              outage={outage}
              canMutate={canMutate}
              onNavigate={onNavigate}
            />

            {/* Operator actions — dispatcher+ only */}
            {canMutate ? (
              <div className="border-t border-gray-100 pt-4">
                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">
                  Operator actions
                </div>
                <ActionBar outage={outage} onDone={() => { /* invalidation refetches */ }} />
              </div>
            ) : (
              <div className="border-t border-gray-100 pt-4 text-xs text-gray-400">
                Read-only view. Operator actions require the dispatcher role.
              </div>
            )}

            {/* Affected meters (paged) */}
            <div className="border-t border-gray-100 pt-4">
              <AffectedMeters outageId={outage.id} />
            </div>

            {/* Timeline */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Timeline</h4>
              <Timeline outageId={outage.id} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
